/**
 * LiveChannel — "CH 09 · YOU'RE ON AIR".
 *
 * Opt-in: the visitor's webcam becomes the background source. It flows
 * through the exact same pipeline as the clips (glitchGL presets, the dice,
 * the Off Air scramble), so the site does to *you* what it does to its
 * footage. Everything stays on-device: the stream is a local MediaStream
 * and is never recorded, uploaded or drawn anywhere except this canvas.
 *
 * The mic (if granted) drives an analyser: transients → glitch spikes, so
 * talking or clapping tears the picture.
 *
 *   live.toggle()  →  on/off   (key 9, the camera button)
 *   'randomize' (dice) and any rotateSource() tune away from the channel.
 */

const MIN_GAP_MS = 260;        // throttle between audio-driven spikes
const ONSET_RATIO = 1.55;      // rms must jump this much over its running avg

export class LiveChannel {
  constructor({ background, button = null, osd = null }) {
    this.bg = background;
    this.btn = button;
    this.osd = osd;
    this.stream = null;
    this.on = false;
    this._ac = null; this._an = null; this._buf = null;
    this._avg = 0; this._lastSpike = 0; this._raf = 0;

    this.btn?.addEventListener('click', () => this.toggle());
    document.addEventListener('keydown', (e) => {
      if (e.key !== '9' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (document.body.classList.contains('is-world-open')) return;
      this.toggle();
    });
    // The dice / any clip change tunes away from the live channel.
    document.addEventListener('bg:source-change', () => { if (this.on) this.disable({ silent: true }); });
    document.addEventListener('visibilitychange', () => { if (document.hidden && this.on) this.disable(); });
  }

  get supported() { return !!navigator.mediaDevices?.getUserMedia; }

  async toggle() { return this.on ? this.disable() : this.enable(); }

  async enable() {
    if (this.on) return true;
    if (!this.supported) { this.osd?.show('CH 09', 'no camera'); return false; }
    this.osd?.show('CH 09', 'requesting camera…');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 540 } },
        audio: true,
      }).catch(async (err) => {
        // Mic refused or absent: take the camera alone.
        if (err?.name === 'NotAllowedError') throw err;
        return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      });
    } catch (err) {
      this.osd?.show('CH 09', err?.name === 'NotAllowedError' ? 'camera refused' : 'no signal');
      return false;
    }
    this.stream = stream;
    this.on = true;
    document.body.classList.add('is-live');
    this._syncButton();
    this.bg.setLiveStream(stream);
    this.osd?.show('CH 09', "you're on air · local only", { accent: true });
    this._startAnalyser(stream);
    stream.getVideoTracks()[0]?.addEventListener('ended', () => this.disable());
    return true;
  }

  disable({ silent = false } = {}) {
    if (!this.on) return false;
    this.on = false;
    document.body.classList.remove('is-live');
    this._stopAnalyser();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this._syncButton();
    if (!silent) {
      this.bg.setLiveStream(null);
      this.osd?.show('CH 09', 'off air');
    }
    return false;
  }

  _syncButton() {
    if (!this.btn) return;
    this.btn.setAttribute('aria-pressed', String(this.on));
    this.btn.setAttribute('aria-label', this.on ? 'Go off air (stop camera)' : "Go on air (use your camera as the channel — never leaves your device)");
    this.btn.title = this.on ? 'Off air' : "You're on air";
  }

  // ── audio-reactive glitch ────────────────────────────────────────────
  _startAnalyser(stream) {
    if (!stream.getAudioTracks().length) return;
    try {
      this._ac = new (window.AudioContext || window.webkitAudioContext)();
      const src = this._ac.createMediaStreamSource(stream);
      this._an = this._ac.createAnalyser();
      this._an.fftSize = 512;
      this._an.smoothingTimeConstant = 0.5;
      src.connect(this._an);                       // analyser only — never to the speakers
      this._buf = new Uint8Array(this._an.fftSize);
      this._avg = 0;
      this._tick();
    } catch { /* no audio context — picture still works */ }
  }

  _stopAnalyser() {
    cancelAnimationFrame(this._raf);
    this._raf = 0;
    this._ac?.close().catch(() => {});
    this._ac = this._an = this._buf = null;
  }

  _tick = () => {
    if (!this._an) return;
    this._raf = requestAnimationFrame(this._tick);
    this._an.getByteTimeDomainData(this._buf);
    let sum = 0;
    for (let i = 0; i < this._buf.length; i++) { const d = (this._buf[i] - 128) / 128; sum += d * d; }
    const rms = Math.sqrt(sum / this._buf.length);
    this._avg = this._avg * 0.94 + rms * 0.06;
    const now = performance.now();
    if (rms > 0.05 && rms > this._avg * ONSET_RATIO && now - this._lastSpike > MIN_GAP_MS) {
      this._lastSpike = now;
      const k = Math.min(3.2, 0.6 + (rms - this._avg) * 12);
      this.bg.triggerGlitch({ intensity: k });
      document.body.style.setProperty('--live-level', k.toFixed(2));
    }
  };
}
